import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import { toastMessage } from "shared/components/common/toast";
import ReviewStep from "shared/components/questionnaire/review";
import QuestionnaireStep1 from "shared/components/questionnaire/step1";
import QuestionnaireStep10 from "shared/components/questionnaire/step10";
import QuestionnaireStep2 from "shared/components/questionnaire/step2";
import QuestionnaireStep3 from "shared/components/questionnaire/step3";
import QuestionnaireStep4 from "shared/components/questionnaire/step4";
import QuestionnaireStep5 from "shared/components/questionnaire/step5";
import QuestionnaireStep6 from "shared/components/questionnaire/step6";
import QuestionnaireStep7 from "shared/components/questionnaire/step7";
import QuestionnaireStep8 from "shared/components/questionnaire/step8";
import QuestionnaireStep9 from "shared/components/questionnaire/step9";
import ConfirmationModal from "shared/modal/confirmation";
import SuccessModal from "shared/modal/success";
import { setQuestionnaireReducer } from "shared/redux/reducers/questionnaire";
import { setQuestionsReducer } from "shared/redux/reducers/questions";
import { routeConstant } from "shared/routes/routeConstant";
import {
  AddDraftEvaluationsService,
  AddEvaluationsService,
  GetArchiveCountService,
} from "shared/services/evaluations";
import {
  GetAppOutcomesService,
  GetQuestionsService,
} from "shared/services/questionnaire";
import {
  checkDropdownOptions,
  checkIfComplete,
  checkIfWarning,
  getPercentagefromString,
  mergeArrays,
} from "shared/utils/helper";
import styles from "./style.module.scss";
import { setArchiveCountReducer } from "shared/redux/reducers/archiveCount";
import { useCallbackPrompt } from "shared/hooks/useCallbackPrompt";
import LeaveAlert from "shared/modal/leave";

const Questionnaire = () => {
  const {
    questionnaire: { formData, lastStep },
  } = useSelector((state: any) => state?.root);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef<any>(null);
  const finalPayload = useRef<any>({});
  const finalDraftPayload = useRef<any>({});
  const stepRef = useRef<number>(1);

  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [addEvent, setAddEvent] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [localFormData, setLocalFormData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [draftLoading, setDraftLoading] = useState<boolean>(false);
  const loadingRef = useRef<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [initial, setInitial] = useState<boolean>(true);

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    showAlert && addEvent
  );
  const [jobCodeError, setJobCodeError] = useState<any[]>([
    {
      message: "",
      error: false,
    },
  ]);

  const handleGetCount = () => {
    GetArchiveCountService()
      .then(({ data }) => {
        dispatch(setArchiveCountReducer({ count: data }));
      })
      .catch((err) => {});
  };

  const handleShowSuccessModal = () => {
    setShowSuccessModal(true);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleGetQuestions = () => {
    GetQuestionsService()
      .then(({ data }) => {
        dispatch(setQuestionsReducer({ questions: data }));
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleSubmission = () => {
    setLoading(true);
    setShowAlert(false); //Stop Listening for Navigation Change
    AddEvaluationsService(finalPayload.current)
      .then(() => {
        handleShowSuccessModal();
        finalPayload.current = {};
        formikRef.current.resetForm();
        setLocalFormData([]);
        dispatch(setQuestionnaireReducer({ formData: [], lastStep: 1 }));
        handleGetCount();
        setStep(1);
        stepRef.current = 1;
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
        let errorType = err?.response?.data?.errorType;
        if (errorType) {
          let data: any[] = err?.response?.data?.data;
          setStep(1);
          if (errorType === 1) {
            for (let i = 0; i < formikRef.current?.values.jobs?.length; i++) {
              let job = formikRef.current?.values.jobs[i];
              if (data.includes(job?.jobCode)) {
                formikRef.current?.setFieldError(
                  `jobs[${i}].jobCode`,
                  "Duplicate Job Code"
                );
              }
            }
          } else if (errorType === 2) {
            for (let i = 0; i < formikRef.current?.values.jobs?.length; i++) {
              let job = formikRef.current?.values.jobs[i];
              if (data.includes(job?.jobCode)) {
                formikRef.current?.setFieldError(
                  `jobs[${i}].jobCode`,
                  "Job Code Already Exists"
                );
              }
            }
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDaftFormData = async (finalArr: any) => {
    setDraftLoading(true);
    setShowAlert(false); //Stop Listening for Navigation Change
    let tempData: any[] = [];
    for (let i = 0; i < finalArr?.length; i++) {
      let job = finalArr[i];
      if (job?.roleType?.value === "1") {
        let tempObj: any = {
          jobTitle: job?.jobTitle,
          department: job?.department,
          roleType: job?.roleType?.value,
        };
        if (step >= 2 && step <= 10 && job?.q1) {
          if (job?.q1?.modifier?.value) {
            tempObj.modifier = job?.q1?.modifier?.value;
          }
          if (job?.q1?.levelWork?.value) {
            tempObj.levelOfWork = job?.q1?.levelWork?.value;
          }
        }
        if (step >= 3 && step <= 10 && job?.q2?.institutionalExpertise?.value) {
          tempObj.q2 = job?.q2?.institutionalExpertise?.value;
        }
        if (step >= 4 && step <= 10 && job?.q3?.culturalImpact?.value) {
          tempObj.q3 = job?.q3?.culturalImpact?.value;
        }
        if (step >= 5 && step <= 10 && job?.q4?.physicalEnvironment?.value) {
          tempObj.q4 = job?.q4?.physicalEnvironment?.value;
        }
        if (step >= 6 && step <= 10 && job?.q5?.mentalEnvironment?.value) {
          tempObj.q5 = job?.q5?.mentalEnvironment?.value;
        }
        if (step >= 7 && step <= 10 && job?.q6?.applicationLevel?.value) {
          tempObj.q6 = job?.q6?.applicationLevel?.value;
        }
        if (step >= 8 && step <= 10 && job?.q7?.manualEffort?.value) {
          tempObj.q7 = job?.q7?.manualEffort?.value;
        }
        if (step >= 9 && step <= 10 && job?.q8?.outcomes?.value) {
          tempObj.q8 = job?.q8?.outcomes?.value;
        }
        if (step === 10 && job?.q9?.notes) {
          let code = `IC | ${job?.q1?.output} - ${job?.q2?.institutionalExpertise?.level}${job?.q3?.culturalImpact?.level} - ${job?.q4?.physicalEnvironment?.level}${job?.q5?.mentalEnvironment?.level} - ${job?.q6?.applicationLevel?.level} - ${job?.q7?.manualEffort?.level} - ${job?.q8?.outcomes?.level}`;
          let q1 = Number(job?.q1?.output);
          let q2 = getPercentagefromString(job?.q2?.output);
          let q3 = getPercentagefromString(job?.q3?.output);
          let q4 = Number(job?.q4?.output);
          let q5 = Number(job?.q5?.output);
          let q6 = getPercentagefromString(job?.q6?.output);
          let q7 = Number(job?.q7?.output);
          let q8 = getPercentagefromString(job?.q8?.output);
          let result = ((q1 * q2 * q3 + (q4 + q5)) * q6 + q7) * q8;
          tempObj.q9 = job?.q9?.notes;
          tempObj.shortCode = code;
          tempObj.rasScore = Math.trunc(result);
          tempObj.rasLevel = 7;
        }
        if (job?.jobCode) {
          tempObj.jobCode = job?.jobCode;
        }
        if (job?.jobGrade) {
          tempObj.jobGrade = job?.jobGrade;
        }
        tempData.push(tempObj);
      } else {
        let tempObj: any = {
          jobTitle: job?.jobTitle,
          department: job?.department,
          roleType: job?.roleType?.value,
        };
        if (step >= 2 && step <= 10 && job?.q1) {
          if (job?.q1?.modifier?.value) {
            tempObj.modifier = job?.q1?.modifier?.value;
          }
          if (job?.q1?.levelWork?.value) {
            tempObj.levelOfWork = job?.q1?.levelWork?.value;
          }
        }
        if (step >= 3 && step <= 10 && job?.q2?.institutionalExpertise?.value) {
          tempObj.q2 = job?.q2?.institutionalExpertise?.value;
        }
        if (step >= 4 && step <= 10 && job?.q3?.culturalImpact?.value) {
          tempObj.q3 = job?.q3?.culturalImpact?.value;
        }
        if (step >= 5 && step <= 10 && job?.q4?.physicalEnvironment?.value) {
          tempObj.q4 = job?.q4?.physicalEnvironment?.value;
        }
        if (step >= 6 && step <= 10 && job?.q5?.mentalEnvironment?.value) {
          tempObj.q5 = job?.q5?.mentalEnvironment?.value;
        }
        if (step >= 7 && step <= 10 && job?.q6?.applicationLevel?.value) {
          tempObj.q6 = job?.q6?.applicationLevel?.value;
        }
        if (step >= 8 && step <= 10 && job?.q7?.manualEffort?.value) {
          tempObj.q7 = job?.q7?.manualEffort?.value;
        }
        if (step >= 9 && step <= 10 && job?.q8?.outcomes?.value) {
          tempObj.q8 = job?.q8?.outcomes?.value;
        }
        if (step === 10 && job?.q9?.notes) {
          let code = `M | ${job?.q1?.output} - ${job?.q2?.institutionalExpertise?.level}${job?.q3?.culturalImpact?.level} - ${job?.q4?.physicalEnvironment?.level}${job?.q5?.mentalEnvironment?.level} - ${job?.q6?.applicationLevel?.level} - ${job?.q7?.manualEffort?.level} - ${job?.q8?.outcomes?.level}`;
          let q1 = Number(job?.q1?.output);
          let q2 = getPercentagefromString(job?.q2?.output);
          let q3 = getPercentagefromString(job?.q3?.output);
          let q4 = Number(job?.q4?.output);
          let q5 = Number(job?.q5?.output);
          let q6 = getPercentagefromString(job?.q6?.output);
          let q7 = Number(job?.q7?.output);
          let response = await GetAppOutcomesService({
            level: job?.q6?.applicationLevel?.level,
            outcome: job?.q8?.outcomes?.level,
          });
          let q8 = getPercentagefromString(response?.data?.outcome);
          let result = ((q1 * q2 * q3 + (q4 + q5)) * q6 + q7) * q8;
          tempObj.q9 = job?.q9?.notes;
          tempObj.shortCode = code;
          tempObj.rasScore = Math.trunc(result);
          tempObj.rasLevel = 7;
        }
        if (job?.jobCode) {
          tempObj.jobCode = job?.jobCode;
        }
        if (job?.jobGrade) {
          tempObj.jobGrade = job?.jobGrade;
        }
        tempData.push(tempObj);
      }
      if (i === finalArr.length - 1) {
        finalDraftPayload.current = {
          evals: tempData,
        };
        AddDraftEvaluationsService(finalDraftPayload.current)
          .then(() => {
            toastMessage("success", "Evaluation saved as draft");
            finalPayload.current = {};
            formikRef.current.resetForm();
            setLocalFormData([]);
            stepRef.current = 1;
            setStep(1);

            dispatch(setQuestionnaireReducer({ formData: [], lastStep: 1 }));
            navigate(routeConstant.draftEvaluation.path);
          })
          .catch((err) => {
            toastMessage("error", err?.response?.data?.error);
          })
          .finally(() => {
            setDraftLoading(false);
          });
      }
    }
  };

  const handleSetFormData = (finalArr: any) => {
    setLocalFormData(finalArr);
    dispatch(setQuestionnaireReducer({ formData: finalArr, lastStep: step }));
  };

  const handleDraftSubmission = (finalArr: any) => {
    handleDaftFormData(finalArr);
  };

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    handleGetQuestions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialLoading) {
      if (formData?.length > 0) {
        setLocalFormData(formData);
        setStep(lastStep);
      }
      setInitialLoading(false);
    }
    // eslint-disable-next-line
  }, [formData]);

  useEffect(() => {
    if (initial) {
      setInitial(false);
    } else {
      if (showAlert && addEvent) {
        setShowAlert(true);
      } else {
        setShowAlert(false);
      }
    }
    // eslint-disable-next-line
  }, [showAlert, addEvent]);

  return (
    <section
      className={classNames(
        "relative h-full flex flex-col justify-between gap-4"
      )}
    >
      <div className={classNames(styles.mainContainer)}>
        {step === 1 ? (
          <QuestionnaireStep1
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
            setJobCodeError={setJobCodeError}
            jobCodeError={jobCodeError}
          />
        ) : step === 2 ? (
          <QuestionnaireStep2
            formikRef={formikRef}
            formData={localFormData}
            setStep={setStep}
            setLoading={setLoading}
            setFormData={handleSetFormData}
            stepRef={stepRef.current}
            setAddEvent={setAddEvent}
          />
        ) : step === 3 ? (
          <QuestionnaireStep3
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 4 ? (
          <QuestionnaireStep4
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 5 ? (
          <QuestionnaireStep5
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 6 ? (
          <QuestionnaireStep6
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 7 ? (
          <QuestionnaireStep7
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 8 ? (
          <QuestionnaireStep8
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 9 ? (
          <QuestionnaireStep9
            formikRef={formikRef}
            formData={localFormData}
            setLoading={setLoading}
            setAddEvent={setAddEvent}
          />
        ) : step === 10 ? (
          <QuestionnaireStep10
            formikRef={formikRef}
            formData={localFormData}
            setAddEvent={setAddEvent}
          />
        ) : step === 11 ? (
          <ReviewStep
            formData={localFormData}
            setStep={setStep}
            finalPayload={finalPayload}
            setLoading={setLoading}
          />
        ) : null}
      </div>
      <div className={classNames(styles.actionsContainer)}>
        {step > 1 ? (
          <CustomButton
            Icon={Icons.ArrowLeft}
            IconDirction="left"
            containerStyle={classNames(styles.prevButton)}
            onClick={() => {
              if (step > 1) {
                let finalArr = mergeArrays(
                  step,
                  localFormData,
                  formikRef.current.values?.jobs
                );
                setLocalFormData(finalArr);
                stepRef.current = step - 1;
                dispatch(
                  setQuestionnaireReducer({
                    formData: finalArr,
                    lastStep: stepRef.current,
                  })
                );
                setStep(step - 1);
              }
            }}
            disabled={step === 1 || loading || draftLoading}
          />
        ) : (
          <div />
        )}
        <div className={classNames("w-full flex items-center justify-center")}>
          <CustomPagination
            currentPage={step}
            totalCount={10}
            pageSize={1}
            displayArrow={false}
            disabled={loading}
            onPageChange={(page: any) => {
              if (!loading && !draftLoading) {
                if (page < step) {
                  let finalArr = mergeArrays(
                    step,
                    localFormData,
                    formikRef.current.values?.jobs
                  );
                  setLocalFormData(finalArr);
                  stepRef.current = page;
                  dispatch(
                    setQuestionnaireReducer({
                      formData: finalArr,
                      lastStep: page,
                    })
                  );
                  setStep(page);
                } else {
                  formikRef.current.submitForm();
                  //immediate next page
                  setTimeout(() => {
                    if (
                      formikRef.current?.errors &&
                      // eslint-disable-next-line
                      Object.keys(formikRef.current.errors).length == 0 &&
                      !loadingRef.current &&
                      jobCodeError.every((item) => !item.error)
                    ) {
                      if (
                        checkIfWarning(formikRef.current.values?.jobs, step)
                      ) {
                        let finalArr = mergeArrays(
                          step,
                          localFormData,
                          formikRef.current.values?.jobs
                        );
                        if (
                          checkDropdownOptions(finalArr) ||
                          page === step + 1
                        ) {
                          setLocalFormData(finalArr);
                          stepRef.current = page;
                          dispatch(
                            setQuestionnaireReducer({
                              formData: finalArr,
                              lastStep: page,
                            })
                          );
                          setStep(page);
                        }
                      } else {
                        handleShowConfirmationModal();
                      }
                    }
                  }, 100);
                }
              }
            }}
          />
        </div>
        <div className={classNames("flex items-center gap-2")}>
          <CustomButton
            title={step === 10 ? "Review" : step === 11 ? "Submit" : "Continue"}
            Icon={Icons.ArrowRight}
            IconDirction="right"
            onClick={() => {
              if (step === 11) {
                if (checkIfComplete(formData)) {
                  handleSubmission();
                } else {
                  toastMessage("error", "Please fill all the fields");
                }
              } else {
                formikRef.current.submitForm();
                setTimeout(() => {
                  if (
                    formikRef.current?.errors &&
                    // eslint-disable-next-line
                    Object.keys(formikRef.current.errors).length == 0 &&
                    !loadingRef.current &&
                    jobCodeError.every((item) => !item.error)
                  ) {
                    if (checkIfWarning(formikRef.current.values?.jobs, step)) {
                      let finalArr = mergeArrays(
                        step,
                        localFormData,
                        formikRef.current.values?.jobs
                      );
                      stepRef.current = step + 1;
                      setLocalFormData(finalArr);
                      dispatch(
                        setQuestionnaireReducer({
                          formData: finalArr,
                          lastStep: stepRef.current,
                        })
                      );
                      if (step < 11) {
                        setStep(step + 1);
                      }
                    } else {
                      handleShowConfirmationModal();
                    }
                  }
                }, 100);
              }
            }}
            containerStyle={classNames(styles.nextButton)}
            disabled={loading || draftLoading}
            loading={loading}
          />
          {step > 1 && step < 10 ? (
            <CustomButton
              title="Save Draft"
              loading={draftLoading}
              onClick={() => {
                let finalArr = mergeArrays(
                  step,
                  localFormData,
                  formikRef.current.values?.jobs
                );
                setLocalFormData(finalArr);
                dispatch(
                  setQuestionnaireReducer({
                    formData: finalArr,
                    lastStep: stepRef.current,
                  })
                );
                handleDraftSubmission(finalArr);
              }}
              disabled={loading || draftLoading}
              containerStyle={classNames(styles.saveDraftButton)}
            />
          ) : null}
        </div>
      </div>
      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        actionButtonText="Yes, Continue"
        cancelButtonText="No, Cancel"
        title="Improbable combination found!"
        subtitle="Are you sure you want to proceed?"
        handleSubmit={() => {
          let finalArr = mergeArrays(
            step,
            localFormData,
            formikRef.current.values?.jobs
          );
          stepRef.current = step + 1;
          setLocalFormData(finalArr);
          dispatch(
            setQuestionnaireReducer({
              formData: finalArr,
              lastStep: stepRef.current,
            })
          );
          if (step < 11) {
            setStep(step + 1);
          }
          handleCloseConfirmationModal();
        }}
      />
      <LeaveAlert
        show={showPrompt && addEvent && showAlert}
        handleClose={cancelNavigation}
        handleSubmit={confirmNavigation}
      />
    </section>
  );
};

export default Questionnaire;
